import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { Pagination } from '@/common/types';
import { API_URL, IUnappliedPushInvoiceDto } from '@/dtos';
import { InvoiceTypeView } from '@/modules/employer/components/ReconciliationReports/components/FinOpsInvoicing/types/invoicing.constants';
import { IInvoiceType } from '@/types';

import { UnpaidInvoice } from '../UnappliedFunds.types';

export const UNPAID_INVOICES_QUERY_KEY = 'UNPAID_INVOICES_QUERY';

interface Props {
  organizationId?: string;
  searchInput?: string;
  invoiceTypes?: IInvoiceType[];
  page: number;
  size: number;
}

const fetchUnpaidInvoices = ({ organizationId, searchInput, invoiceTypes, page, size }: Props) =>
  api.get<Pagination<IUnappliedPushInvoiceDto>>(API_URL.INVOICES_SEARCH_UNAPPLIED_PUSH, {
    params: {
      organizationId: Number(organizationId),
      searchInput: searchInput || undefined,
      invoiceTypes: invoiceTypes?.length ? invoiceTypes.join(',') : undefined,
      page,
      size,
    },
  });

const formatInvoice = (invoice: IUnappliedPushInvoiceDto): UnpaidInvoice => ({
  id: String(invoice.invoice_id),
  type: (invoice.invoice_type && InvoiceTypeView[invoice.invoice_type]) ?? '-',
  date: invoice.invoice_date ? dayjs(invoice.invoice_date).format(DEFAULT_DATE_FORMAT) : '-',
  amount: invoice.amount ?? 0,
});

const useUnpaidInvoicesQuery = ({ organizationId, searchInput, invoiceTypes, page, size }: Props) => {
  const { data, isLoading } = useQuery(
    [UNPAID_INVOICES_QUERY_KEY, organizationId, searchInput, invoiceTypes, page, size],
    () => fetchUnpaidInvoices({ organizationId, searchInput, invoiceTypes, page, size }),
    { enabled: Boolean(organizationId) }
  );

  const invoices = useMemo(() => (data?.data?.content ?? []).map(formatInvoice), [data?.data?.content]);

  return {
    invoices,
    total: data?.data?.totalElements ?? 0,
    isLoading,
  };
};

export default useUnpaidInvoicesQuery;
